import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Play } from 'lucide-react';

import { FormTextInput } from '@/components/form-fields/form-text-input';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import TransshipmentTransfersField from '@/routes/(app)/(scenarios)/(create)/components/transshipment-transfers-field';
import {
  CreateScenarioSchema,
  SIMULATION_DURATION_DAYS_MAX,
  SIMULATION_DURATION_DAYS_MIN,
} from '@/routes/(app)/(scenarios)/(create)/types';

interface ConfigureSimulationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: () => void | Promise<void>;
  isLoading: boolean;
  hasError: boolean;
  isSubmitting: boolean;
}

const ConfigureSimulationDialog: FC<ConfigureSimulationDialogProps> = ({
  open,
  onOpenChange,
  onSubmit,
  isLoading,
  hasError,
  isSubmitting,
}) => {
  const form = useFormContext<CreateScenarioSchema>();
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-4xl'>
        <DialogHeader>
          <DialogTitle>{t('scenarios.create.configure.title')}</DialogTitle>
          <DialogDescription>
            {t('scenarios.create.configure.description')}
          </DialogDescription>
        </DialogHeader>
        <div className='grid grid-cols-3 items-start gap-4'>
          <FormTextInput
            type='number'
            control={form.control}
            name='simulationRuns'
            label={t('scenarios.create.configure.simulationRuns')}
          />
          <FormTextInput
            type='datetime-local'
            control={form.control}
            name='startTime'
            label={t('scenarios.create.configure.startTime')}
          />
          <FormTextInput
            type='number'
            control={form.control}
            name='durationDays'
            label={t('scenarios.create.configure.durationDays')}
            description={t('scenarios.create.configure.durationHelper', {
              min: SIMULATION_DURATION_DAYS_MIN,
              max: SIMULATION_DURATION_DAYS_MAX,
            })}
          />
          <FormTextInput
            type='number'
            control={form.control}
            name='teuCapacity'
            label={t('scenarios.create.configure.teuCapacity')}
          />
          <FormTextInput
            type='number'
            control={form.control}
            name='reeferCapacity'
            label={t('scenarios.create.configure.reeferCapacity')}
          />
        </div>
        <Separator />
        <TransshipmentTransfersField />
        {hasError && (
          <p className='text-sm text-destructive'>
            {t('scenarios.create.configure.loadError')}
          </p>
        )}
        <Separator />
        <DialogFooter>
          <Button
            variant='secondary'
            onClick={() => {
              onOpenChange(false);
            }}>
            {t('scenarios.create.configure.cancel')}
          </Button>
          <Button
            disabled={isLoading || hasError || isSubmitting}
            onClick={form.handleSubmit(async () => {
              await onSubmit();
            })}>
            <Play />
            {t('scenarios.create.configure.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfigureSimulationDialog;
